import { useEffect, useRef, useState } from 'react';

import { TelegramAuthV5Response, TelegramConfigResponse } from 'app/api/services/telegram-sign/types';

import { API, ResponseStatus } from 'app/api';

const INTERVAL = 3000;

type UseTelegramSignArgs = {
  onSuccess?: () => void;
  onProcessing?: () => void;
  onFail?: () => void;
};

type Status = 'POLING' | 'FAILED_LOAD_CONFIG' | 'READY' | 'FAILED_SESSION_EXPIRED' | 'SUCCESS';

type UseTelegramSignReturnType = {
  status: Status;
  loading: boolean;
  start: () => void;
};

//В ответе бэка прилетает sessionExpired через минуту, но на всякий случай на фронте тоже считает с запасом в 80 сек,
// чтобы если бэк по какой-то причине не вернет то что нужно, завершим поллинг на фронте
const MAX_POLING_TIME = 80_000;

export function useTelegramSign({
  onSuccess,
  onProcessing,
  onFail,
}: UseTelegramSignArgs): UseTelegramSignReturnType {
  const startedRef = useRef<number>();
  const intervalRef = useRef<NodeJS.Timer>();
  const configRef = useRef<TelegramConfigResponse | null>();
  const statusRef = useRef<Status>('READY');
  const setStatus = (val: Status) => (statusRef.current = val);
  const [loading, setLoading] = useState(false);

  const intervalWorker = async () => {
    if (!configRef.current) {
      return;
    }

    const status = statusRef.current;

    if (
      status === 'FAILED_LOAD_CONFIG'
      || status === 'FAILED_SESSION_EXPIRED'
      || status === 'SUCCESS'
    ) {
      clearInterval(intervalRef.current);
      onSuccess?.();
      setLoading(false);
      return;
    }

    const now = new Date().getTime();
    const started = startedRef.current || 0;

    const response = await API.telegramSign.tryTelegramAuth(configRef.current.startGuid);

    if (response.status === ResponseStatus.SUCCESS) {
      startedRef.current = 0;
      setStatus('SUCCESS');
      onSuccess?.();
    }

    if ((response.status === ResponseStatus.UNPROCESSABLE_ENTITY
      && isTelegramResponse(response.data)
      && response.data.type === 'sessionExpired')
      || now - started > MAX_POLING_TIME
    ) {
      startedRef.current = 0;
      setStatus('FAILED_SESSION_EXPIRED');
      onFail?.();
    }
  };

  const start = async () => {
    const response = await API.telegramSign.getTelegramAuthConfig();

    if (response.status !== 200) {
      setStatus('FAILED_LOAD_CONFIG');
      return;
    }

    configRef.current = response.data;
    const url = getUrl(configRef.current);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    if (configRef.current) {
      setLoading(true);
      navigateLink(url);
      onProcessing?.();
      setStatus('POLING');
      startedRef.current = new Date().getTime();
      intervalRef.current = setInterval(intervalWorker, INTERVAL);
    }
  };

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    status: statusRef.current,
    loading,
    start,
  };
}

function getUrl(config: TelegramConfigResponse) {
  return `${config.botLink}?start=${config.startGuid}`;
}

function navigateLink(url: string) {
  window.open(url, '_blank');
}

function isTelegramResponse(data: unknown): data is TelegramAuthV5Response {
  return data instanceof Object && 'type' in data && 'detail' in data;
}
